import React from 'react'
import Add from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Remove from '@mui/icons-material/Remove'
import { Box, Button, IconButton, LinearProgress, SxProps, TextField, Tooltip, Typography, buttonClasses, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Feature } from '@cbgms/base/feature-flags'

interface IIncrementDecrementButtonProps {
  lockButtons: boolean
  isLoading: boolean
  quantity: number
  deductOrDeleteProductRow?: () => Promise<void | unknown>
  setQuantity?: (value: React.SetStateAction<number>) => void
  deleteProductRow?: () => Promise<void | unknown>
  handleInputFinalizeByBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  handleInputFinalizeByKey?: (event: React.KeyboardEvent<HTMLDivElement>, value: string) => void
  incrementProductRow?: () => Promise<void | unknown>
  incrementQuantity?: number
  size?: 'small' | 'medium'
  styling?: SxProps
  translationNames: 'workorder' | 'order'
  productPromotionType?: string
  totalStock?: number
  showDeleteOrder?: boolean
  isOpEqualOpPromotionType?: boolean
}

const IncrementDecrementButton: React.FC<IIncrementDecrementButtonProps> = props => {
  return (
    <Feature feature='more-products-on-screen-design-v2' fallback={<IncrementDecrementButtonOld {...props} />}>
      <IncrementDecrementButtonNew {...props} />
    </Feature>
  )
}

const IncrementDecrementButtonOld: React.FC<IIncrementDecrementButtonProps> = ({
  lockButtons,
  isLoading,
  quantity,
  deductOrDeleteProductRow,
  setQuantity,
  handleInputFinalizeByBlur,
  handleInputFinalizeByKey,
  incrementProductRow,
  incrementQuantity,
  size,
  styling,
  translationNames,
  totalStock,
  isOpEqualOpPromotionType = false
}) => {
  const { t } = useTranslation()
  const minWidthButton = 32
  const theme = useTheme()
  const productTotalStock = totalStock ?? 0

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        ...styling
      }}
    >
      <Button
        aria-label='-'
        data-testid='orderbutton-deduct'
        size={size || 'small'}
        variant='outlined'
        className='main-col-text'
        disabled={lockButtons || !deductOrDeleteProductRow}
        onClick={deductOrDeleteProductRow}
        sx={{
          borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
          minWidth: minWidthButton
        }}
      >
        <Tooltip
          placement='top'
          data-testid='orderbutton-remove-from-basket'
          title={
            <Typography variant='caption'>
              {translationNames === 'order' ? t('order.article-remove-from-basket') : t('order.article-remove-from-workorder')}
            </Typography>
          }
        >
          {incrementQuantity && quantity <= incrementQuantity ? (
            <DeleteIcon fontSize={size || 'small'} data-testid='orderbutton-icon-delete' />
          ) : (
            <Remove fontSize={size || 'small'} data-testid='orderbutton-icon-remove' />
          )}
        </Tooltip>
      </Button>
      <Box display='inline-flex' position='relative' maxWidth={64}>
        <TextField
          data-testid='orderbutton-input'
          size={size || 'small'}
          type='text'
          variant='outlined'
          inputProps={{ 'data-testid': 'order-input-quantity' }}
          value={quantity}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!e.currentTarget.value) setQuantity?.(0)
            const _input = parseInt(e.currentTarget.value)
            if (isNaN(_input)) return
            setQuantity?.(_input)
          }}
          disabled={lockButtons || !setQuantity}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          onBlur={handleInputFinalizeByBlur}
          onKeyDown={e => {
            // Event element type with onKeyDown is HTMLDivElement instead of HTMLInputElement
            // This doesn't contain currentTarget.value.
            // Hoever it does have a target.value.
            const { value } = e.target as HTMLInputElement
            handleInputFinalizeByKey && handleInputFinalizeByKey(e, value)
          }}
          sx={{
            display: 'block',
            position: 'relative',
            zIndex: '2',
            borderRadius: 0,
            '& > *': {
              height: '100%'
            },
            '& fieldset': {
              borderRadius: '0 !important'
            },
            '& input': {
              textAlign: 'center',
              p: 0.38
            }
          }}
        />
        {isLoading && (
          <LinearProgress color='muted' sx={{ position: 'absolute', width: '100%', zIndex: '1', bottom: '0', maxWidth: '64px' }} />
        )}
      </Box>
      <Tooltip
        placement='top'
        data-testid='orderbutton-increment-in-basket'
        title={
          <Typography variant='caption'>
            {isOpEqualOpPromotionType && quantity >= productTotalStock
              ? t('order.article-maximum-quantity-reached')
              : t('order.article-increment-quantity')}
          </Typography>
        }
      >
        <Box sx={{ display: 'flex', cursor: 'pointer', minWidth: minWidthButton }}>
          <Button
            aria-label='+'
            data-testid='orderbutton-increment'
            variant='outlined'
            disabled={lockButtons || !incrementProductRow || (isOpEqualOpPromotionType && quantity >= productTotalStock)}
            size={size || 'small'}
            className='main-col-bg white-text'
            onClick={incrementProductRow}
            sx={{
              borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
              minWidth: minWidthButton,
              [`&.${buttonClasses.disabled}`]: {
                color: '#989fa5 !important',
                backgroundColor: '#cdd7df !important'
              }
            }}
          >
            <Add fontSize={size || 'small'} />
          </Button>
        </Box>
      </Tooltip>
    </Box>
  )
}
const IncrementDecrementButtonNew: React.FC<IIncrementDecrementButtonProps> = ({
  lockButtons,
  isLoading,
  quantity,
  deductOrDeleteProductRow,
  setQuantity,
  deleteProductRow,
  handleInputFinalizeByBlur,
  handleInputFinalizeByKey,
  incrementProductRow,
  size,
  styling,
  translationNames,
  totalStock,
  showDeleteOrder,
  isOpEqualOpPromotionType = false
}) => {
  const { t } = useTranslation()
  const minWidthButton = 32
  const theme = useTheme()
  const productTotalStock = totalStock ?? 0

  function DeleteOrderButton({ onClick }: { onClick?: () => Promise<unknown> }) {
    return (
      <Box
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', minWidth: '2rem', minHeight: '1rem', justifyContent: 'flex-end' }}
        data-testid='delete-icon'
      >
        <Tooltip
          placement='top'
          data-testid='orderbutton-remove-from-basket'
          title={
            <Typography variant='caption'>
              {translationNames === 'order' ? t('order.article-remove-from-basket') : t('order.article-remove-from-workorder')}
            </Typography>
          }
        >
          <IconButton onClick={onClick} data-testid='orderbutton-icon-delete' sx={{ padding: '0' }}>
            <DeleteIcon sx={{ fill: 'rgb(50, 50, 50)' }} />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }
  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        ...styling
      }}
    >
      <Button
        aria-label='-'
        data-testid='orderbutton-deduct'
        size={size || 'small'}
        variant='outlined'
        className='main-col-text'
        disabled={lockButtons || !deductOrDeleteProductRow}
        onClick={deductOrDeleteProductRow}
        sx={{
          borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
          minWidth: minWidthButton,
          minHeight: '2.2rem',
          [`&.${buttonClasses.disabled}`]: {
            color: '#989fa5 !important',
            backgroundColor: '#cdd7df !important'
          }
        }}
      >
        <Tooltip
          placement='top'
          data-testid='orderbutton-remove-from-basket'
          title={
            <Typography variant='caption'>
              {translationNames === 'order' ? t('order.article-decrement-quantity') : t('order.article-remove-from-workorder')}
            </Typography>
          }
        >
          <Remove fontSize={size || 'small'} data-testid='orderbutton-icon-remove' />
        </Tooltip>
      </Button>
      <Box display='inline-flex' position='relative' maxWidth={64}>
        <TextField
          data-testid='orderbutton-input'
          size={size || 'small'}
          type='text'
          variant='outlined'
          inputProps={{ 'data-testid': 'order-input-quantity' }}
          value={quantity}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!e.currentTarget.value) {
              setQuantity?.(0)
            } else {
              const _input = parseInt(e.currentTarget.value)
              if (isNaN(_input)) return
              setQuantity?.(_input)
            }
          }}
          disabled={lockButtons || !setQuantity}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          onBlur={handleInputFinalizeByBlur}
          onKeyDown={e => {
            // Event element type with onKeyDown is HTMLDivElement instead of HTMLInputElement
            // This doesn't contain currentTarget.value.
            // Hoever it does have a target.value.
            const { value } = e.target as HTMLInputElement
            handleInputFinalizeByKey && handleInputFinalizeByKey(e, value)
          }}
          sx={{
            display: 'block',
            position: 'relative',
            zIndex: '2',
            width: '3rem',
            borderRadius: 0,
            '& > *': {
              height: '100%'
            },
            '& fieldset': {
              borderRadius: '0 !important'
            },
            '& input': {
              textAlign: 'center',
              p: 0.38
            }
          }}
        />
        {isLoading && (
          <LinearProgress color='muted' sx={{ position: 'absolute', width: '100%', zIndex: '1', bottom: '0', maxWidth: '64px' }} />
        )}
      </Box>
      <Tooltip
        placement='top'
        data-testid='orderbutton-increment-in-basket'
        title={
          <Typography variant='caption'>
            {isOpEqualOpPromotionType && quantity >= productTotalStock
              ? t('order.article-maximum-quantity-reached')
              : t('order.article-increment-quantity')}
          </Typography>
        }
      >
        <Box sx={{ display: 'flex', cursor: 'pointer', minWidth: minWidthButton }}>
          <Button
            aria-label='+'
            data-testid='orderbutton-increment'
            variant='outlined'
            disabled={lockButtons || !incrementProductRow || (isOpEqualOpPromotionType && quantity >= productTotalStock)}
            size={size || 'small'}
            className='main-col-bg white-text'
            onClick={incrementProductRow}
            sx={{
              borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
              minWidth: minWidthButton,
              [`&.${buttonClasses.disabled}`]: {
                color: '#989fa5 !important',
                backgroundColor: '#cdd7df !important'
              }
            }}
          >
            <Add fontSize={size || 'small'} />
          </Button>
        </Box>
      </Tooltip>
      {showDeleteOrder && (
        <DeleteOrderButton
          onClick={async () => {
            await deleteProductRow?.()
          }}
        />
      )}
    </Box>
  )
}

export default IncrementDecrementButton
